/**
 * The Discogs adapter.
 *
 * One of `enrichment/providers/`' four adapters, and by some distance the largest. It lived inside
 * `app/api/lookup/discogs/route.ts` — 828 lines, the biggest file in `src/` and well over
 * `CLAUDE.md`'s 500-line limit — until #407. The route now keeps its `GET`, its `getUser()` guard,
 * its 401 and a delegation (ADR 0010), and `enrich.ts` calls `lookupMusic` in process rather than
 * over a cookie-less HTTP call to the app's own origin (#78).
 *
 * **`hasDiscogsCredentials` is published because the 503 is the route's to return and the
 * knowledge is the provider's.** #407 names it provider vocabulary for exactly that reason.
 *
 * **This module must not import `@/account/server`.** The session check belongs to the route.
 *
 * **It is still over the line limit**, and #407 asks for a split by subject — search, artist,
 * label, release. That split did not land here; see the pull request body, which records it as the
 * one open criterion rather than claiming it.
 */

import { trackError, isDevelopment } from '@/lib/errors'
import type {
  DiscogsSearchResult,
  DiscogsArtist,
  DiscogsLabel,
  DiscogsRelease,
  DiscogsMaster,
  DiscogsLabelInfo,
  DiscogsUnifiedResult,
} from './discogs-api'

const DISCOGS_API_BASE = 'https://api.discogs.com'

// Rate limiting: Discogs allows 60 requests per minute for authenticated requests
const USER_AGENT = 'Konstantout/1.0'
const REQUEST_TIMEOUT_MS = 10000 // 10 second timeout per request

/** Error messages */
const DISCOGS_ERRORS = {
  timeout: 'Discogs API request timed out',
  rateLimit: 'Too many requests. Please wait a moment and try again.',
  unauthorized: 'Discogs API credentials invalid or missing',
  serverError: 'Discogs API is temporarily unavailable',
  network: 'Failed to connect to Discogs',
} as const

/**
 * Clean up Discogs text formatting.
 * Removes markup like [a=Artist Name], [l=Label Name], [i]...[/i], [b]...[/b], [url=...]...[/url]
 */
function cleanDiscogsText(text: string | undefined): string | undefined {
  if (!text) return undefined

  return (
    text
      // [a=Artist Name] or [a123] → Artist Name or remove
      .replace(/\[a=([^\]]+)\]/g, '$1')
      .replace(/\[a\d+\]/g, '')
      // [l=Label Name] or [l123] → Label Name or remove
      .replace(/\[l=([^\]]+)\]/g, '$1')
      .replace(/\[l\d+\]/g, '')
      // [m=Master Name] or [m123] → Master Name or remove
      .replace(/\[m=([^\]]+)\]/g, '$1')
      .replace(/\[m\d+\]/g, '')
      // [r=Release Name] or [r123] → Release Name or remove
      .replace(/\[r=([^\]]+)\]/g, '$1')
      .replace(/\[r\d+\]/g, '')
      // [i]italic[/i] → italic
      .replace(/\[i\](.*?)\[\/i\]/g, '$1')
      // [b]bold[/b] → bold
      .replace(/\[b\](.*?)\[\/b\]/g, '$1')
      // [u]underline[/u] → underline
      .replace(/\[u\](.*?)\[\/u\]/g, '$1')
      // [url=...]text[/url] → text
      .replace(/\[url=[^\]]*\](.*?)\[\/url\]/g, '$1')
      // [url]link[/url] → link
      .replace(/\[url\](.*?)\[\/url\]/g, '$1')
      // Clean up any remaining square bracket tags
      .replace(/\[\/?[a-z]+\]/gi, '')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ')
      .trim()
  )
}

/**
 * Check if Discogs API credentials are configured.
 * Supports either:
 * - DISCOGS_API_KEY (personal access token)
 * - DISCOGS_CONSUMER_KEY + DISCOGS_CONSUMER_SECRET (OAuth)
 */
export function hasDiscogsCredentials(): boolean {
  return !!(
    process.env.DISCOGS_API_KEY ||
    (process.env.DISCOGS_CONSUMER_KEY && process.env.DISCOGS_CONSUMER_SECRET)
  )
}

/**
 * Make a request to Discogs API with timeout handling
 */
async function discogsRequest(endpoint: string): Promise<Response> {
  const personalToken = process.env.DISCOGS_API_KEY
  const consumerKey = process.env.DISCOGS_CONSUMER_KEY
  const consumerSecret = process.env.DISCOGS_CONSUMER_SECRET

  const headers: HeadersInit = {
    'User-Agent': USER_AGENT,
  }

  let url = `${DISCOGS_API_BASE}${endpoint}`

  // Add authentication - required for most Discogs API endpoints
  if (personalToken) {
    // Personal access token method (simpler)
    headers['Authorization'] = `Discogs token=${personalToken}`
  } else if (consumerKey && consumerSecret) {
    // OAuth consumer key/secret method
    const separator = url.includes('?') ? '&' : '?'
    url += `${separator}key=${consumerKey}&secret=${consumerSecret}`
  } else {
    if (isDevelopment) {
      console.warn(
        'Discogs API credentials not configured. Set DISCOGS_API_KEY or DISCOGS_CONSUMER_KEY/SECRET.',
      )
    }
  }

  // Add timeout using AbortController
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      headers,
      signal: controller.signal,
    })
    return response
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Create a mock response for timeout
      return new Response(JSON.stringify({ error: DISCOGS_ERRORS.timeout }), {
        status: 408,
        headers: { 'Content-Type': 'application/json' },
      })
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Build optimized Discogs search URL with type-specific parameters
 * Uses Discogs API's specific parameters (artist, label, release_title, title)
 * instead of generic 'q' parameter for more accurate results
 */
function buildDiscogsSearchUrl(
  query: string,
  typeHint?: 'artist' | 'label' | 'release' | null,
  artistName?: string,
): string {
  const params = new URLSearchParams()
  params.append('per_page', '10')

  // Use type-specific parameters for better results
  if (typeHint === 'artist') {
    // For artists, use q parameter with type=artist (more reliable than artist= parameter)
    // The artist= parameter can be too restrictive and return 0 results
    params.append('type', 'artist')
    params.append('q', query)
    if (isDevelopment)
      console.log('[Discogs] Optimized search: type=artist, q=', query)
  } else if (typeHint === 'label') {
    // For labels, use q parameter with type=label (more reliable than label= parameter)
    params.append('type', 'label')
    params.append('q', query)
    if (isDevelopment)
      console.log('[Discogs] Optimized search: type=label, q=', query)
  } else if (typeHint === 'release') {
    // For releases/albums, use master type for better results (masters group all versions)
    params.append('type', 'master')
    if (artistName) {
      // Use q parameter with "Artist - Release Title" format for better matching
      // More flexible than title= parameter which can be too restrictive
      params.append('q', `${artistName} ${query}`)
      if (isDevelopment) {
        console.log(
          '[Discogs] Optimized search: type=master, q=',
          `${artistName} ${query}`,
        )
      }
    } else {
      // Use q parameter for release title search (more flexible than release_title=)
      params.append('q', query)
      if (isDevelopment)
        console.log('[Discogs] Optimized search: type=master, q=', query)
    }
  } else {
    // Fallback to generic query for mixed searches
    params.append('q', query)
    if (isDevelopment) console.log('[Discogs] Generic search: q=', query)
  }

  return `/database/search?${params.toString()}`
}

/**
 * Search Discogs with optimized parameters based on type hint
 */
async function searchDiscogsAll(
  query: string,
  typeHint?: 'artist' | 'label' | 'release' | null,
  artistName?: string,
): Promise<{ results: DiscogsSearchResult[]; error?: string }> {
  const searchUrl = buildDiscogsSearchUrl(query, typeHint, artistName)
  const response = await discogsRequest(searchUrl)

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    trackError(new Error(`Discogs search failed: ${response.status}`), {
      provider: 'discogs',
      status: response.status,
      errorText: errorText.slice(0, 200),
    })

    // Check for auth error
    if (response.status === 401) {
      return {
        results: [],
        error:
          'Discogs API credentials invalid. Check your DISCOGS_API_KEY or DISCOGS_CONSUMER_KEY/SECRET.',
      }
    }

    return { results: [], error: `Discogs API error: ${response.status}` }
  }

  const data = await response.json()

  // Log concise search summary in development
  if (isDevelopment) {
    console.log('[Discogs API] Search:', {
      query,
      typeHint,
      resultsCount: data.results?.length || 0,
      totalItems: data.pagination?.items || 0,
      topResults: data.results?.slice(0, 3).map((r: DiscogsSearchResult) => ({
        id: r.id,
        title: r.title,
        type: r.type,
      })),
    })
  }

  return { results: data.results || [] }
}

/**
 * Map Discogs result type to our media type
 * @param discogsType - The type from Discogs API
 * @param typeHint - Optional hint from the request to distinguish artist vs label vs release
 */
function mapDiscogsType(
  discogsType: string,
  typeHint?: 'artist' | 'label' | 'release' | null,
): 'artist' | 'label' | 'release' | null {
  switch (discogsType) {
    case 'artist':
      return 'artist'
    case 'label':
      return 'label'
    case 'release':
    case 'master':
      // If user explicitly asked for 'release' (album), return 'release'
      // Otherwise default to 'release' since releases are albums
      return 'release'
    default:
      return null
  }
}

async function getArtistDetails(
  artistId: number,
  logDetails = false,
): Promise<DiscogsArtist | null> {
  const response = await discogsRequest(`/artists/${artistId}`)

  if (!response.ok) {
    trackError(new Error('Discogs artist details fetch failed'), {
      provider: 'discogs',
      artistId,
      status: response.status,
    })
    return null
  }

  const data = await response.json()

  // Only log if explicitly requested (for best match only) and in development
  if (logDetails && isDevelopment) {
    console.log('[Discogs API] Artist details:', {
      artistId,
      name: data.name,
      hasImages: !!data.images?.length,
      imageCount: data.images?.length || 0,
    })
  }

  return data
}

async function getArtistReleases(
  artistId: number,
  logDetails = false,
): Promise<DiscogsRelease[]> {
  const response = await discogsRequest(
    `/artists/${artistId}/releases?per_page=20`,
  )

  if (!response.ok) {
    trackError(new Error('Discogs artist releases fetch failed'), {
      provider: 'discogs',
      artistId,
      status: response.status,
    })
    return []
  }

  const data = await response.json()

  // Only log summary if explicitly requested (for best match only) and in development
  if (logDetails && isDevelopment) {
    console.log('[Discogs API] Artist releases:', {
      artistId,
      releasesCount: data.releases?.length || 0,
      totalPages: data.pagination?.pages || 0,
      sampleLabels: data.releases
        ?.slice(0, 5)
        .map((r: DiscogsRelease) => r.label || r.labels?.[0]?.name)
        .filter(Boolean),
    })
  }

  return data.releases || []
}

async function getLabelDetails(
  labelId: number,
  logDetails = false,
): Promise<DiscogsLabel | null> {
  const response = await discogsRequest(`/labels/${labelId}`)

  if (!response.ok) {
    if (logDetails) {
      trackError(new Error('Discogs label details fetch failed'), {
        provider: 'discogs',
        labelId,
        status: response.status,
      })
    }
    return null
  }

  const data = await response.json()

  // Only log if explicitly requested and in development
  if (logDetails && isDevelopment) {
    console.log('[Discogs API] Label details:', {
      labelId,
      name: data.name,
    })
  }

  return data
}

async function getLabelReleases(
  labelId: number,
  logDetails = false,
): Promise<DiscogsRelease[]> {
  const response = await discogsRequest(
    `/labels/${labelId}/releases?per_page=20`,
  )

  if (!response.ok) {
    if (logDetails) {
      trackError(new Error('Discogs label releases fetch failed'), {
        provider: 'discogs',
        labelId,
        status: response.status,
      })
    }
    return []
  }

  const data = await response.json()

  // Only log if explicitly requested and in development
  if (logDetails && isDevelopment) {
    console.log('[Discogs API] Label releases:', {
      labelId,
      releasesCount: data.releases?.length || 0,
    })
  }

  return data.releases || []
}

async function getReleaseDetails(
  releaseId: number,
  logDetails = false,
): Promise<DiscogsRelease | null> {
  const response = await discogsRequest(`/releases/${releaseId}`)

  if (!response.ok) {
    if (logDetails) {
      trackError(new Error('Discogs release details fetch failed'), {
        provider: 'discogs',
        releaseId,
        status: response.status,
      })
    }
    return null
  }

  const data = await response.json()

  // Only log if explicitly requested and in development
  if (logDetails && isDevelopment) {
    console.log('[Discogs API] Release details:', {
      releaseId,
      title: data.title,
      labels: data.labels?.map((l: { id: number; name: string }) => ({
        id: l.id,
        name: l.name,
      })),
    })
  }

  return data
}

async function getMasterDetails(
  masterId: number,
  logDetails = false,
): Promise<DiscogsMaster | null> {
  const response = await discogsRequest(`/masters/${masterId}`)

  if (!response.ok) {
    if (logDetails) {
      trackError(new Error('Discogs master details fetch failed'), {
        provider: 'discogs',
        masterId,
        status: response.status,
      })
    }
    return null
  }

  const data = await response.json()

  // Only log if explicitly requested and in development
  if (logDetails && isDevelopment) {
    console.log('[Discogs API] Master details:', {
      masterId,
      title: data.title,
      mainRelease: data.main_release,
    })
  }

  return data
}

/**
 * Enrich a single search result with full details based on its type
 * @param shouldLog - If true, enables detailed logging (only for best match)
 */
async function enrichResult(
  result: DiscogsSearchResult,
  typeHint?: 'artist' | 'label' | 'release' | null,
  shouldLog = false,
): Promise<DiscogsUnifiedResult | null> {
  const mediaType = mapDiscogsType(result.type, typeHint)
  if (!mediaType) return null

  try {
    if (result.type === 'artist') {
      // Simplified: only fetch artist details, no releases lookup
      const artistDetails = await getArtistDetails(result.id, shouldLog)

      // Get image from artist details first, then fallback to search result images
      const artistImage =
        artistDetails?.images?.find((img) => img.type === 'primary')?.uri ||
        artistDetails?.images?.[0]?.uri ||
        result.cover_image ||
        result.thumb ||
        undefined

      // Use profile field for bio/description (AI fallback handled in enrich-media route)
      const bio = artistDetails?.profile
        ? cleanDiscogsText(artistDetails.profile.slice(0, 500))
        : undefined

      const enrichedResult = {
        type: 'artist' as const,
        externalId: String(result.id),
        name: artistDetails?.name || result.title,
        bio,
        imageUrl: artistImage,
        genres: undefined, // No genres from artist details alone
        labelNames: [],
        labelInfo: [], // No labels without fetching releases
      }

      if (shouldLog && isDevelopment) {
        console.log('[Discogs API] Enriched artist:', {
          name: enrichedResult.name,
          hasBio: !!bio,
          hasImage: !!artistImage,
        })
      }

      return enrichedResult
    }

    if (result.type === 'label') {
      const [labelDetails, releases] = await Promise.all([
        getLabelDetails(result.id, shouldLog),
        getLabelReleases(result.id, shouldLog),
      ])

      // Extract unique genres and artists from releases
      const genresSet = new Set<string>()
      const artistsMap = new Map<string, string>() // id -> name

      releases.forEach((release) => {
        release.genres?.forEach((g) => genresSet.add(g))
        release.styles?.forEach((s) => genresSet.add(s))
        release.artists?.forEach((a) => {
          if (a.id && a.name && !artistsMap.has(String(a.id))) {
            artistsMap.set(String(a.id), a.name)
          }
        })
      })

      // Get image from label details first, then fallback to search result images
      const labelImage =
        labelDetails?.images?.find((img) => img.type === 'primary')?.uri ||
        labelDetails?.images?.[0]?.uri ||
        result.cover_image ||
        result.thumb ||
        undefined

      const enrichedResult = {
        type: 'label' as const,
        externalId: String(result.id),
        name: labelDetails?.name || result.title,
        profile: cleanDiscogsText(labelDetails?.profile?.slice(0, 500)),
        imageUrl: labelImage,
        website: labelDetails?.urls?.[0],
        genres: Array.from(genresSet).slice(0, 10),
        artistNames: Array.from(artistsMap.values()).slice(0, 10),
      }

      // Logging removed to reduce spam - only log summaries in final response

      return enrichedResult
    }

    if (result.type === 'release') {
      const releaseDetails = await getReleaseDetails(result.id, shouldLog)

      // Get image from release details or search result
      const releaseImage =
        releaseDetails?.images?.[0]?.uri ||
        result.cover_image ||
        result.thumb ||
        undefined

      // Use the mapped type (could be 'release' depending on typeHint)
      const enrichedResult = {
        type: mediaType as 'release',
        externalId: String(result.id),
        name: releaseDetails?.title || result.title,
        artistName: releaseDetails?.artists?.[0]?.name,
        labelName: releaseDetails?.labels?.[0]?.name,
        year: releaseDetails?.year,
        genres: [
          ...(releaseDetails?.genres || []),
          ...(releaseDetails?.styles || []),
        ],
        imageUrl: releaseImage,
      }

      // Logging removed to reduce spam - only log summaries in final response

      return enrichedResult
    }

    if (result.type === 'master') {
      // For masters, get master details first, then optionally get main release for complete data
      const masterDetails = await getMasterDetails(result.id, shouldLog)

      // If master has a main_release, get that for more complete data (especially labels)
      let releaseDetails: DiscogsRelease | null = null
      if (masterDetails?.main_release) {
        releaseDetails = await getReleaseDetails(
          masterDetails.main_release,
          shouldLog,
        )
      }

      // Use release details if available (more complete), otherwise use master details
      const sourceData = releaseDetails || masterDetails

      // Get image from multiple sources: release images, master images, or search result
      const masterImage =
        releaseDetails?.images?.[0]?.uri ||
        masterDetails?.images?.[0]?.uri ||
        result.cover_image ||
        result.thumb ||
        undefined

      // Use the mapped type (could be 'release' depending on typeHint)
      const enrichedResult = {
        type: mediaType as 'release',
        externalId: String(result.id), // Keep master ID as external ID
        name: sourceData?.title || result.title,
        artistName: sourceData?.artists?.[0]?.name,
        labelName: releaseDetails?.labels?.[0]?.name, // Labels only available from release
        year: sourceData?.year,
        genres: [...(sourceData?.genres || []), ...(sourceData?.styles || [])],
        imageUrl: masterImage,
      }

      // Logging removed to reduce spam - only log summaries in final response

      return enrichedResult
    }
  } catch (error) {
    trackError(error, {
      provider: 'discogs',
      action: 'enrichResult',
      resultType: result.type,
      resultId: result.id,
    })
  }

  return null
}

export interface MusicLookupOptions {
  /** `artist`, `label` or `release`, when the caller knows which */
  typeHint?: 'artist' | 'label' | 'release' | null
  /** The artist name, for a label or release search */
  artistName?: string
  /** Filter to the hinted type rather than merely ordering by it */
  isExplicitType?: boolean
}

/**
 * The best Discogs match for a query, plus alternatives.
 *
 * The whole of what the route used to compute inside its `GET`. It assumes credentials are
 * present — the route checks `hasDiscogsCredentials()` first and returns its own 503.
 */
export async function lookupMusic(
  query: string,
  {
    typeHint = null,
    artistName,
    isExplicitType = false,
  }: MusicLookupOptions = {},
) {
  // Search with optimized parameters based on type hint
  const { results: searchResults, error } = await searchDiscogsAll(
    query,
    typeHint as 'artist' | 'label' | 'release' | null,
    artistName,
  )

  if (error) {
    // The route turns this into a 503; the provider states it as a value (#407).
    return { error, bestMatch: null, alternatives: [] }
  }

  if (searchResults.length === 0) {
    return { bestMatch: null, alternatives: [] }
  }

  // Filter or prioritize based on type hint
  let filteredResults = searchResults
  if (typeHint) {
    // Map our types to Discogs types for filtering
    // 'release' maps to Discogs 'master' (preferred) and 'release'
    const discogsTypes =
      typeHint === 'release'
        ? ['master', 'release'] // Prioritize masters for better album matching
        : [typeHint]

    if (isExplicitType) {
      // User explicitly said "artist", "label", "release", or "album" - filter to only that type
      filteredResults = searchResults.filter((r) =>
        discogsTypes.includes(r.type),
      )
    } else {
      // Prioritize matching types at the top, with masters first for releases/tracks
      if (typeHint === 'release') {
        filteredResults = [
          ...searchResults.filter((r) => r.type === 'master'),
          ...searchResults.filter((r) => r.type === 'release'),
          ...searchResults.filter((r) => !discogsTypes.includes(r.type)),
        ]
      } else {
        filteredResults = [
          ...searchResults.filter((r) => discogsTypes.includes(r.type)),
          ...searchResults.filter((r) => !discogsTypes.includes(r.type)),
        ]
      }
    }
  }

  if (filteredResults.length === 0) {
    return { bestMatch: null, alternatives: [] }
  }

  // Enrich top 3 results in parallel for better UX
  // This allows the user to pick from multiple enriched options
  // Only log details for the first (best) match to reduce log spam
  const topResults = filteredResults.slice(0, 3)
  const enrichedResults = await Promise.all(
    topResults.map((r, index) => {
      // Only enable detailed logging for the first result (best match)
      const shouldLog = index === 0
      return enrichResult(
        r,
        typeHint as 'artist' | 'label' | 'release' | null,
        shouldLog,
      )
    }),
  )

  // Find the first successful enrichment as best match
  const validResults = enrichedResults.filter(
    (r): r is DiscogsUnifiedResult => r !== null,
  )

  if (validResults.length === 0) {
    // Try enriching more results if first 3 failed
    for (const result of filteredResults.slice(3)) {
      const enriched = await enrichResult(
        result,
        typeHint as 'artist' | 'label' | 'release' | null,
      )
      if (enriched) {
        return { bestMatch: enriched, alternatives: [] }
      }
    }
    return { bestMatch: null, alternatives: [] }
  }

  // First valid result is best match, rest are alternatives
  const [bestMatch, ...enrichedAlternatives] = validResults

  // Add more alternatives from remaining unenriched results (basic info only)
  const additionalAlternatives: DiscogsUnifiedResult[] = filteredResults
    .slice(validResults.length)
    .slice(0, 5 - enrichedAlternatives.length)
    .map((r) => ({
      type:
        mapDiscogsType(
          r.type,
          typeHint as 'artist' | 'label' | 'release' | null,
        ) || 'release',
      externalId: String(r.id),
      name: r.title,
      imageUrl: r.cover_image || r.thumb,
    }))

  const response = {
    bestMatch,
    alternatives: [...enrichedAlternatives, ...additionalAlternatives],
  }

  // Log concise summary of final response in development
  if (isDevelopment) {
    console.log('[Discogs API] Final response:', {
      query,
      typeHint,
      bestMatch: bestMatch
        ? {
            type: bestMatch.type,
            name: bestMatch.name,
            hasImage: !!bestMatch.imageUrl,
            genresCount: bestMatch.genres?.length || 0,
            labelsCount: bestMatch.labelInfo?.length || 0,
            labelNames:
              bestMatch.labelInfo?.map((l) => l.name).slice(0, 3) || [],
          }
        : null,
      alternativesCount: response.alternatives.length,
    })
  }

  return response
}
